import { useCallback, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { B400 } from "@atlaskit/theme/colors";

import { useLocalizedHref } from "@/libraries/web-framework";
import { Row } from "@/libraries/wpl/foundations/src";
import { Play } from "@/libraries/wpl/foundations/src/icons/object/play";
import { TypographyStyles } from "@/libraries/wpl/foundations/src/typography-styles";
import { YoutubeModal } from "@/patterns/modal/src";

export const ImpossibleAloneBanner = () => {
    const intl = useIntl();
    const [isOpen, setIsOpen] = useState(false);
    const openModal = useCallback(() => setIsOpen(true), []);
    const closeModal = useCallback(() => setIsOpen(false), []);

    const headerText = intl.formatMessage({
        id: "LiftedBar.impossible-alone.header",
        defaultMessage: "Impossible Alone"
    });
    const description = intl.formatMessage({
        id: "LiftedBar.impossible-alone.description",
        defaultMessage: "See how collaboration makes the impossible, possible"
    });
    const ctaText = intl.formatMessage({
        id: "LiftedBar.impossible-alone.cta",
        defaultMessage: "Play video"
    });
    const videoUrl = useLocalizedHref(
        "https://www.youtube.com/watch?v=CTETwaQ1dqI"
    );

    const bannerWrapperStyles = css({
        "width": "100%",
        "position": "relative",
        [media.only.sm]: {
            paddingInlineStart: "64px",
            paddingInlineEnd: "64px",
            paddingBlockEnd: "20px"
        },
        [media.above.md]: {
            margin: "0 auto",
            maxWidth: "1160px",
            paddingInlineStart: "96px",
            paddingInlineEnd: "96px"
        },
        ".row-layout": {
            borderRadius: "3px",
            boxShadow: "1px 1px 6px 0px #0000001A",
            backgroundColor: "#fff",
            padding: 0,
            gap: 0
        },

        ".image-container": {
            backgroundImage:
                "url(//wac-cdn.atlassian.com/misc-assets/webp-images/Impossible-Alone-Video-Header-Image-smile.webp-647850140.webp)",
            backgroundPosition: "center",
            backgroundSize: "cover",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "193px",
            height: "100%",
            borderRadius: "3px 3px 0 0",
            [media.only.sm]: {
                minHeight: "204px",
                borderRadius: "3px 0 0 3px"
            },
            [media.above.md]: {
                minHeight: "182px",
                borderRadius: "3px 0 0 3px"
            }
        },
        ".text-wrapper": {
            padding: "40px",
            h2: {
                marginBlockStart: 0
            }
        },
        ".video-link": {
            ":hover": {
                svg: {
                    fill: "#e4e4e4"
                }
            },
            "svg": {
                transition: "all .3s",
                fill: "#fff"
            }
        },
        ".video-cta": {
            display: "flex",
            alignItems: "center",
            svg: {
                fill: B400
            }
        },
        ".cta-icon-wrapper": {
            display: "flex",
            paddingInlineEnd: "12px"
        }
    });

    return (
        <TypographyStyles>
            <Row>
                <div className="banner-wrapper" css={bannerWrapperStyles}>
                    <Row layout="4-8">
                        <a
                            href={videoUrl}
                            data-event="clicked"
                            data-event-component="linkButton"
                            data-event-container="videoLink"
                            data-label-english="Play video"
                            onClick={(event) => {
                                event.preventDefault();
                                openModal();
                            }}
                            className="video-link"
                        >
                            <div
                                className="image-container"
                                role="img"
                                aria-label="smiley face formed by hundreds of people"
                            >
                                <div className="icon-wrapper">
                                    <Play size="60" />
                                </div>
                            </div>
                        </a>
                        <div className="text-wrapper">
                            <h2 className="h2">{headerText}</h2>
                            <p className="h5">{description}</p>
                            <a
                                className="video-cta link"
                                href={videoUrl}
                                data-event="clicked"
                                data-event-component="linkButton"
                                data-event-container="videoLink"
                                data-label-english="Play video"
                                onClick={(event) => {
                                    event.preventDefault();
                                    openModal();
                                }}
                            >
                                <div className="cta-icon-wrapper">
                                    <Play size="14" />
                                </div>
                                <span
                                    className="link"
                                    style={{ color: `${B400}` }}
                                >
                                    {ctaText}
                                </span>
                            </a>
                        </div>
                    </Row>
                </div>
            </Row>
            <YoutubeModal
                isOpen={isOpen}
                closeModal={closeModal}
                src="https://www.youtube.com/embed/CTETwaQ1dqI?autoplay=1&amp;rel=0&amp;origin=https%3A%2F%2Fwww.atlassian.com&amp;widget_referrer=https%3A%2F%2Fwww.atlassian.com&amp;enablejsapi=1&amp;widgetid=1&autoplay=1"
                title={intl.formatMessage({
                    id: "LiftedBar.impossible-alone.ModalYouTubeIframeTitle",
                    defaultMessage:
                        "Impossible Alone. Possible Together | Atlassian"
                })}
                closeIconLabel={intl.formatMessage({
                    id: "LiftedBar.impossible-alone.CloseModalButtonMobileLabel",
                    defaultMessage: "Close modal"
                })}
            />
        </TypographyStyles>
    );
};
